import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import { listNotifications, markAllNotificationsRead, markNotificationRead } from '../api/notifications';
import type { HubNotification } from '../api/notifications';
import { notificationsStore, type NotificationsState } from '../lib/notificationsStore';
import { openRequestCount } from '../lib/openRequestCount';
import { POLL_INTERVAL_MS, unreadCount } from '../lib/notifications';

const isVisible = () => document.visibilityState === 'visible';

// A read mark made here outruns its POST when a click also changes the route; keep it while that save
// is in flight, or the row would flash unread again. A failed save puts it back itself.
function keepLocalReadMarks(previous: NotificationsState | null, incoming: HubNotification[]): HubNotification[] {
  return incoming.map((u) => {
    const local = previous?.updates.find((p) => p.id === u.id);
    return !u.readAt && local?.readAt ? { ...u, readAt: local.readAt } : u;
  });
}

// A failed poll keeps the last state on screen; the next tick retries.
async function refresh(getToken: () => Promise<string | null | undefined>, cancelled: () => boolean): Promise<void> {
  try {
    const token = await getToken();
    if (!token || cancelled()) return;
    const { data } = await listNotifications(token);
    if (!data || cancelled()) return;
    notificationsStore.set({ waiting: data.waiting, updates: keepLocalReadMarks(notificationsStore.get(), data.updates) });
    openRequestCount.set(data.waiting.length);
  } catch { /* keep the last state */ }
}

// Mounted once, by the bell. Refreshes on load, on every route change, when the tab comes back and on
// a timer while it is visible.
export function useNotificationsPolling(): void {
  const auth = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    const poll = () => refresh(() => auth.getAccessToken(), () => cancelled);

    const timer = setInterval(() => { if (isVisible()) void poll(); }, POLL_INTERVAL_MS);
    const onVisibility = () => { if (isVisible()) void poll(); };
    document.addEventListener('visibilitychange', onVisibility);
    void poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // auth is deliberately not a dependency: some AuthKit providers give it a new identity on every
    // render, which would restart the poll each time. The route is what restarts it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}

export function useNotifications(): {
  state: NotificationsState | null;
  unread: number;
  markRead(id: string): Promise<void>;
  markAllRead(): Promise<void>;
} {
  const auth = useAuth();
  const state = useSyncExternalStore(notificationsStore.subscribe, notificationsStore.get);

  // Read marks are applied locally first so the dot clears at once; if the save fails they come back.
  const markWhere = useCallback(async (shouldMark: (u: HubNotification) => boolean, save: (token: string) => Promise<unknown>) => {
    const current = notificationsStore.get();
    const marked = current?.updates.filter((u) => !u.readAt && shouldMark(u)).map((u) => u.id) ?? [];
    if (!current || marked.length === 0) return;
    const readAt = new Date().toISOString();
    notificationsStore.set({ ...current, updates: current.updates.map((u) => (marked.includes(u.id) ? { ...u, readAt } : u)) });
    try {
      const token = await auth.getAccessToken();
      if (!token) throw new Error('no token');
      await save(token);
    } catch {
      const latest = notificationsStore.get();
      if (latest) notificationsStore.set({ ...latest, updates: latest.updates.map((u) => (marked.includes(u.id) ? { ...u, readAt: null } : u)) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth identity may change per render
  }, []);

  const markRead = useCallback((id: string) => markWhere((u) => u.id === id, (token) => markNotificationRead(token, id)), [markWhere]);
  const markAllRead = useCallback(() => markWhere(() => true, (token) => markAllNotificationsRead(token)), [markWhere]);

  return { state, unread: unreadCount(state), markRead, markAllRead };
}
